/**
 * Checklist matcher — maps a connected-drive filename to a specific checklist
 * document ID (`DATA_ROOM_DOCUMENTS[].id`). Used after classification so the
 * checklist auto-marks items as 'uploaded' when matching files are detected
 * in the connected data room.
 *
 * Coverage today: ~16 high-confidence patterns. Extend this table as needed.
 */

interface ChecklistMatchRule {
  /** Strong filename / folder pattern. */
  pattern: RegExp;
  /** Target checklist doc ID from DATA_ROOM_DOCUMENTS. */
  docId: string;
}

const RULES: ChecklistMatchRule[] = [
  // Corporate & Legal
  { pattern: /articles[ _-]?of[ _-]?incorp|incorporation[ _-]?(cert|certificate)/i, docId: 'corp-1' },
  { pattern: /articles[ _-]?of[ _-]?association|bylaws/i, docId: 'corp-2' },
  { pattern: /shareholder[ _-]?agreement|sha\b/i, docId: 'corp-3' },
  { pattern: /cap[ _-]?table/i, docId: 'corp-4' },
  { pattern: /board[ _-]?(resolutions?|minutes?)/i, docId: 'corp-5' },
  { pattern: /investor[ _-]?rights?[ _-]?agreement|ira\b/i, docId: 'corp-9' },
  { pattern: /safe[ _-]?(note|agreement)?|convertible[ _-]?note/i, docId: 'corp-13' },
  { pattern: /409a[ _-]?(valuation)?/i, docId: 'corp-14' },
  { pattern: /stock[ _-]?option[ _-]?plan|esop\b/i, docId: 'corp-15' },

  // Financial
  { pattern: /burn[ _-]?(rate)?|runway/i, docId: 'fin-1' },
  { pattern: /p&l|profit[ _-]?and[ _-]?loss/i, docId: 'fin-3' },
  { pattern: /balance[ _-]?sheet/i, docId: 'fin-4' },
  { pattern: /cash[ _-]?flow/i, docId: 'fin-5' },
  { pattern: /financial[ _-]?(model|projection|forecast)/i, docId: 'fin-6' },
  { pattern: /use[ _-]?of[ _-]?funds/i, docId: 'fin-7' },
  { pattern: /unit[ _-]?economics/i, docId: 'fin-10' },

  // Commercial
  { pattern: /pitch[ _-]?deck|investor[ _-]?deck|seed[ _-]?deck|series[ _-]?[a-z][ _-]?deck/i, docId: 'com-1' },
  { pattern: /product[ _-]?roadmap/i, docId: 'com-3' },
  { pattern: /competitive[ _-]?(analysis|landscape)/i, docId: 'com-4' },
  { pattern: /(go[ _-]?to[ _-]?market|gtm)[ _-]?strategy?/i, docId: 'com-9' },

  // HR & Team
  { pattern: /founder[ _-]?(bio|cv|resume)|team[ _-]?bios?|exec[ _-]?bios?/i, docId: 'hr-1' },
];

export interface ChecklistMatchResult {
  docId: string;
  matchedOn: string;
}

/**
 * Match a filename (and optional folder path) to a specific checklist doc.
 * Returns the first matching rule, or undefined when nothing matches.
 */
export function matchChecklistDoc(
  fileName: string,
  folderPath?: string,
): ChecklistMatchResult | undefined {
  const haystack = `${folderPath ?? ''} / ${fileName}`;
  for (const rule of RULES) {
    const m = haystack.match(rule.pattern);
    if (m) return { docId: rule.docId, matchedOn: m[0] };
  }
  return undefined;
}
